import { Component, ElementRef, HostListener, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ConsentService } from './consent';
import { FooterPresence } from './footer/footer';
import { UserService } from './user.service';

/** The top-right menu: site pages plus the account. */
@Component({
  selector: 'app-site-menu',
  imports: [RouterLink],
  template: `
    @if (users.ready() && !users.signedIn()) {
      <button class="primary" (click)="signIn()" [disabled]="busy()">Sign in</button>
    }
    <button
      class="menu-button"
      (click)="open.set(!open())"
      [attr.aria-expanded]="open()"
      aria-haspopup="menu"
      [attr.aria-label]="users.signedIn() ? 'Menu for ' + users.displayName() : 'Menu'"
    >
      @if (users.signedIn()) {
        @if (users.photoUrl(); as photo) {
          <img [src]="photo" alt="" referrerpolicy="no-referrer" />
        } @else {
          <span class="initial">{{ users.displayName()[0] }}</span>
        }
      } @else {
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path
            d="M3 5h14M3 10h14M3 15h14"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
          />
        </svg>
      }
    </button>
    @if (open()) {
      <!-- Closes after any item is chosen; items are links and buttons, so Enter and Space
        reach this handler as clicks too. -->
      <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
      <nav class="menu" role="menu" (click)="open.set(false)">
        @if (users.signedIn()) {
          <div class="menu-label small muted">{{ users.me()?.email || users.displayName() }}</div>
        }
        <a role="menuitem" routerLink="/">New chat</a>
        <a role="menuitem" routerLink="/multi">Compare models</a>
        <hr />
        <a role="menuitem" routerLink="/documentation">Documentation</a>
        <a role="menuitem" routerLink="/status">Network status</a>
        <a role="menuitem" routerLink="/become-a-provider">Run a node</a>
        @if (users.signedIn()) {
          <hr />
          <a role="menuitem" routerLink="/account">Account</a>
          <a role="menuitem" routerLink="/api-keys">API keys</a>
          @if (users.showAdmin()) {
            <!-- A plain link: the admin pages load only when it's followed. -->
            <a role="menuitem" routerLink="/admin">Admin</a>
          }
          <button role="menuitem" (click)="signOut()">Sign out</button>
        }
        @if (!footer.visible()) {
          <hr />
          <div class="legal">
            <a routerLink="/about">About</a>
            <a routerLink="/terms">Terms</a>
            <a routerLink="/privacy">Privacy</a>
            @if (consent.required()) {
              <button (click)="consent.bannerOpen.set(true)">Cookie settings</button>
            }
          </div>
        }
      </nav>
    }
    @if (error()) {
      <span class="error small">{{ error() }}</span>
    }
  `,
  styles: `
    :host {
      position: relative;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .menu-button {
      display: grid;
      place-items: center;
      width: 38px;
      height: 38px;
      padding: 0;
      border-radius: 50%;
      background: none;
      border: none;
      color: var(--fai-color-text);
    }
    img,
    .initial {
      width: 32px;
      height: 32px;
      border-radius: 50%;
    }
    .initial {
      display: grid;
      place-items: center;
      background: var(--fai-color-orchid-70);
      font-weight: 600;
    }
    .menu {
      position: absolute;
      right: 0;
      top: calc(100% + 0.5rem);
      z-index: 100;
      min-width: 220px;
      display: flex;
      flex-direction: column;
      padding: 0.375rem;
      background: var(--fai-color-surface);
      border: 1px solid var(--fai-color-border);
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    }
    .menu-label {
      padding: 0.5rem 0.625rem;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .menu hr {
      margin: 0.375rem 0;
    }
    .menu a,
    .menu button {
      text-align: left;
      padding: 0.5rem 0.625rem;
      border-radius: 6px;
      background: none;
      border: none;
      color: var(--fai-color-text);
      font-size: 0.95rem;
      text-decoration: none;
    }
    .legal {
      display: flex;
      flex-wrap: wrap;
    }
    .menu .legal a,
    .menu .legal button {
      padding: 0.375rem 0.625rem;
      font-size: 0.8rem;
      color: var(--fai-color-text-muted);
    }
    .menu a:hover,
    .menu button:hover {
      background: var(--fai-color-steel-70);
    }
  `,
})
export class SiteMenu {
  users = inject(UserService);
  footer = inject(FooterPresence);
  consent = inject(ConsentService);
  private router = inject(Router);
  private host = inject(ElementRef);
  open = signal(false);
  busy = signal(false);
  error = signal<string | null>(null);

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: MouseEvent) {
    if (!this.host.nativeElement.contains(event.target)) this.open.set(false);
  }

  @HostListener('document:keydown.escape')
  closeOnEscape() {
    this.open.set(false);
  }

  async signIn() {
    this.busy.set(true);
    this.error.set(null);
    try {
      await this.users.signIn();
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        this.error.set('Sign-in failed.');
      }
    } finally {
      this.busy.set(false);
    }
  }

  async signOut() {
    await this.users.signOut();
    this.router.navigate(['/']);
  }
}
